import type { ProjectModel } from '@xver/apis/v1/projects/model';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useActiveProjectScope } from '@xver/hooks/useActiveProject';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TextBadge } from './ProjectBits';

interface ProjectsTableProps {
    projects: ProjectModel[] | undefined;
    isLoading: boolean;
}

export default function ProjectsTable({ projects, isLoading }: ProjectsTableProps) {
    const { t } = useTranslation('projects');
    const { activeProjectId } = useActiveProjectScope();

    const columns = useMemo<DataTableColumn<ProjectModel>[]>(
        () => [
            {
                key: 'name',
                label: t('list.columns.name'),
                render: (project) => (
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <Link
                            to={`/projects/${project.id}`}
                            className="min-h-6 inline-flex items-center font-medium underline-offset-2 hover:underline break-all"
                        >
                            {project.name}
                        </Link>
                        {project.isDefault && <TextBadge>{t('switcher.default')}</TextBadge>}
                        {project.id === activeProjectId && (
                            <TextBadge>{t('list.active')}</TextBadge>
                        )}
                    </div>
                ),
            },
            {
                key: 'role',
                label: t('list.columns.role'),
                render: (project) => t(`roles.${project.role}`),
            },
            {
                key: 'owner',
                label: t('list.columns.owner'),
                render: (project) =>
                    project.role === 'owner' ? t('list.you') : project.owner.name,
            },
            {
                key: 'members',
                label: t('list.columns.members'),
                align: 'right',
                render: (project) => project.memberCount,
            },
        ],
        [t, activeProjectId]
    );

    return (
        <DataTable
            caption={t('list.caption')}
            columns={columns}
            rows={projects}
            isLoading={isLoading}
            emptyMessage={t('list.empty')}
            rowKey={(project) => project.id}
        />
    );
}
